import { Agent, AtpAgent } from '@atproto/api'
import type { OAuthSession } from '@atproto/oauth-client-browser'
import type { AuthorFeedFilter } from '@/types/posts'
import { BLUESKY_PUBLIC_API } from '@/constants/api'
import type { PublicAgentOptions } from './types'
import {
    fetchTimeline,
    likePost,
    unlikePost,
    repostPost,
    unrepostPost,
    deletePost,
    fetchAuthorFeed,
    bookmarkBlueskyPost,
    unbookmarkBlueskyPost,
    fetchBlueskyBookmarks,
} from '@/services/postsService'
import { fetchCustomFeed, fetchSavedFeeds, fetchFeedGeneratorViews } from '@/services/feedsService'
import { fetchListFeed } from '@/services/listsService'
import { fetchBlueskyHashtagPosts } from '@/services/hashtagService'
import { searchBlueskyUsers, searchBlueskyPosts } from '@/services/searchService'
import {
    fetchATNotifications,
    fetchATUnreadCount,
    markATNotificationsSeen,
} from '@/services/notificationsService'
import {
    fetchFollowStatus,
    followUser,
    unfollowUser,
    fetchBlockStatus,
    blockBlueskyUser,
    unblockBlueskyUser,
    fetchBlueskyBlocks,
} from '@/services/socialGraphService'
import { setBlueskyPinnedPost } from '@/services/profileService'
import type {
    PlatformAdapter,
    AdapterSession,
    TimelineOptions,
    SearchResult,
    FeedInfo,
    FollowStatus,
    BlockStatus,
    BlockedUsersPage,
    SocialGraphOptions,
} from './types'

export const blueskyAdapter: PlatformAdapter = {
    platform: 'bluesky',

    capabilities: {
        reply: true,
        like: true,
        repost: true,
        quote: true,
        delete: true,
        follow: true,
        feeds: true,
        hashtags: true,
        gridView: false,
        likedPostsFeed: true,
        favouritesFeed: false,
        profileRepliesTab: true,
        bookmarks: true,
        pinPosts: true,
        lists: true,
    },

    // --- Timeline ---

    async fetchTimeline(session: AdapterSession, options?: TimelineOptions) {
        const s = session as OAuthSession
        if (options?.feedUri) {
            if (options.feedUri.includes('app.bsky.graph.list')) {
                return fetchListFeed(s, options.feedUri, {
                    cursor: options.cursor,
                    limit: options.limit,
                })
            }
            return fetchCustomFeed(s, options.feedUri, {
                cursor: options.cursor,
                limit: options.limit,
            })
        }
        // Use pre-built sessionAgent from AppViewContext (carries atproto-proxy for non-default AppViews).
        // Fall back to raw session agent if none provided (e.g. in test stubs or edge cases).
        const agent = options?.sessionAgent ?? new Agent(s)
        return fetchTimeline(agent, { cursor: options?.cursor, limit: options?.limit })
    },

    // --- Post engagement ---

    async likePost(session: AdapterSession, post) {
        const s = session as OAuthSession
        if (!post.cid) throw new Error('Cannot like post: missing CID')
        const uri = await likePost(s, post.uri, post.cid)
        return { uri, liked: true }
    },

    async unlikePost(session: AdapterSession, post) {
        const s = session as OAuthSession
        if (!post.likeUri) throw new Error('Cannot unlike post: missing likeUri')
        await unlikePost(s, post.likeUri)
    },

    async repostPost(session: AdapterSession, post) {
        const s = session as OAuthSession
        if (!post.cid) throw new Error('Cannot repost post: missing CID')
        const uri = await repostPost(s, post.uri, post.cid)
        return { uri, reposted: true }
    },

    async unrepostPost(session: AdapterSession, post) {
        const s = session as OAuthSession
        if (!post.repostUri) throw new Error('Cannot unrepost post: missing repostUri')
        await unrepostPost(s, post.repostUri)
    },

    async deletePost(session: AdapterSession, post) {
        const s = session as OAuthSession
        await deletePost(s, post.uri)
    },

    // --- Pinned posts ---

    async pinPost(session: AdapterSession, post) {
        const s = session as OAuthSession
        if (!post.cid) throw new Error('Cannot pin post: missing cid')
        // Defense-in-depth: getAuthorFeed reads through the user-selectable public
        // AppView, so a hostile/MITM'd AppView response could hand back an
        // attacker-authored post as an ordinary feed item. The write itself is
        // correctly scoped to the caller's own repo, but nothing else stops an
        // attacker-controlled uri/cid from being written into this account's
        // pinnedPost ref. Require that the post URI (at://<did>/collection/rkey)
        // actually belongs to the signed-in account before writing.
        const postDid = post.uri.split('/')[2]
        if (postDid !== s.did) {
            throw new Error('Cannot pin post: post is not authored by the current account')
        }
        await setBlueskyPinnedPost(s, { uri: post.uri, cid: post.cid })
    },

    async unpinPost(session: AdapterSession, post) {
        const s = session as OAuthSession
        // Pass the post's URI so a stale Unpin cannot clear a different pin.
        await setBlueskyPinnedPost(s, null, post.uri)
    },

    // --- Search ---

    async searchUsers(
        session: AdapterSession,
        query: string,
        _options?: PublicAgentOptions
    ): Promise<SearchResult[]> {
        const s = session as OAuthSession
        // Never forward the AppView-aware publicAgent here: Blacksky's
        // searchActorsTypeahead backend 502s, so people search stays pinned to
        // the default Bluesky AppView regardless of the selection (mackinac#2985).
        const results = await searchBlueskyUsers(query, s)
        return results.map(r => ({
            handle: r.handle ?? r.id,
            displayName: r.displayName ?? r.display,
            avatarUrl: r.avatar,
            platform: 'bluesky' as const,
            did: r.did,
            isVerified: r.isVerified,
        }))
    },

    async searchPosts(
        session: AdapterSession,
        query: string,
        cursor?: string,
        options?: PublicAgentOptions
    ) {
        const s = session as OAuthSession
        return searchBlueskyPosts(query, s, cursor, options?.publicAgent)
    },

    // --- Notifications ---

    async fetchNotifications(session: AdapterSession, options?) {
        const s = session as OAuthSession
        return fetchATNotifications(s, options)
    },

    async getUnreadCount(session: AdapterSession) {
        const s = session as OAuthSession
        const result = await fetchATUnreadCount(s)
        return result.count
    },

    async markNotificationsRead(session: AdapterSession) {
        const s = session as OAuthSession
        await markATNotificationsSeen(s)
    },

    // --- Feeds ---

    async getSavedFeeds(session: AdapterSession): Promise<FeedInfo[]> {
        const s = session as OAuthSession
        const savedFeeds = await fetchSavedFeeds(s)
        const feedGeneratorUris = savedFeeds.filter(f => f.type === 'feed').map(f => f.value)

        const generators =
            feedGeneratorUris.length > 0 ? await fetchFeedGeneratorViews(s, feedGeneratorUris) : []

        const generatorMap = new Map(generators.map(g => [g.uri, g]))
        const resolved: FeedInfo[] = []

        for (const feed of savedFeeds) {
            if (feed.type === 'timeline') continue
            if (feed.type === 'feed') {
                const gen = generatorMap.get(feed.value)
                if (gen) {
                    resolved.push({
                        uri: gen.uri,
                        displayName: gen.displayName,
                        avatar: gen.avatar,
                    })
                }
            }
        }

        return resolved
    },

    // --- Author posts ---

    async fetchAuthorPosts(
        session: AdapterSession,
        identifier: string,
        options?: { cursor?: string; filter?: string; publicAgent?: AtpAgent }
    ) {
        const s = session as OAuthSession
        // Production code (usePosts.ts) calls fetchAuthorFeed directly with publicAgent.
        // This adapter path is only exercised in tests, so we fall back to Bluesky public
        // as a last resort rather than leaving publicAgent required here.
        const publicAgent = options?.publicAgent ?? new AtpAgent({ service: BLUESKY_PUBLIC_API })
        return fetchAuthorFeed(identifier, {
            session: s,
            publicAgent,
            cursor: options?.cursor,
            // The adapter interface's filter is a protocol-agnostic string; narrow to the
            // Bluesky-specific union that fetchAuthorFeed expects.
            filter: options?.filter as AuthorFeedFilter | undefined,
        })
    },

    // --- Follow ---

    async getFollowStatus(
        session: AdapterSession,
        identifier: string,
        options?: SocialGraphOptions
    ): Promise<FollowStatus> {
        const s = session as OAuthSession
        // Use proxy-aware sessionAgent from AppViewContext when provided; fall back to raw
        // session agent when absent (e.g. custom feed paths or test stubs).
        const agent = options?.sessionAgent ?? new Agent(s)
        const status = await fetchFollowStatus(agent, identifier)
        return { isFollowing: !!status.followingUri, followRef: status.followingUri }
    },

    async follow(session: AdapterSession, identifier: string): Promise<FollowStatus> {
        const s = session as OAuthSession
        const uri = await followUser(s, identifier)
        return { isFollowing: true, followRef: uri }
    },

    async unfollow(
        session: AdapterSession,
        _identifier: string,
        followRef?: string
    ): Promise<void> {
        const s = session as OAuthSession
        if (!followRef) throw new Error('Cannot unfollow: missing follow record URI')
        await unfollowUser(s, followRef)
    },

    // --- Block ---

    async getBlockStatus(
        session: AdapterSession,
        identifier: string,
        options?: SocialGraphOptions
    ): Promise<BlockStatus> {
        const s = session as OAuthSession
        const agent = options?.sessionAgent ?? new Agent(s)
        const status = await fetchBlockStatus(agent, identifier)
        return { isBlocked: !!status.blockUri, blockRef: status.blockUri }
    },

    async blockUser(session: AdapterSession, identifier: string): Promise<BlockStatus> {
        const s = session as OAuthSession
        const uri = await blockBlueskyUser(s, identifier)
        return { isBlocked: true, blockRef: uri }
    },

    async unblockUser(
        session: AdapterSession,
        _identifier: string,
        blockRef?: string
    ): Promise<void> {
        const s = session as OAuthSession
        if (!blockRef) throw new Error('Cannot unblock: missing block record URI')
        await unblockBlueskyUser(s, blockRef)
    },

    async getBlockedUsers(
        session: AdapterSession,
        options?: { cursor?: string; limit?: number }
    ): Promise<BlockedUsersPage> {
        const s = session as OAuthSession
        const page = await fetchBlueskyBlocks(s, options)
        return {
            users: page.profiles.map(p => ({
                id: p.did,
                handle: p.handle,
                displayName: p.displayName,
                avatar: p.avatar,
                // blockUri from viewer.blocking is the block record URI needed to unblock.
                // If missing, blockRef is left undefined and the Unblock button is disabled.
                blockRef: p.blockUri,
            })),
            cursor: page.cursor,
        }
    },

    // --- Bookmarks ---

    async bookmarkPost(session: AdapterSession, post) {
        const s = session as OAuthSession
        if (!post.cid) throw new Error('Cannot bookmark post: missing CID')
        const uri = await bookmarkBlueskyPost(s, post.uri, post.cid)
        return { uri, bookmarked: true }
    },

    async unbookmarkPost(session: AdapterSession, post) {
        const s = session as OAuthSession
        await unbookmarkBlueskyPost(s, post.uri)
    },

    async fetchBookmarks(session: AdapterSession, options?) {
        const s = session as OAuthSession
        return fetchBlueskyBookmarks(s, options)
    },

    // --- Hashtags ---

    async fetchHashtagPosts(session: AdapterSession, tag: string, cursor?: string) {
        const s = session as OAuthSession
        return fetchBlueskyHashtagPosts(tag, s, cursor)
    },
}
